import { Controller } from '@hotwired/stimulus';
import { Lithuanian } from 'flatpickr/dist/l10n/lt';
import { english } from 'flatpickr/dist/l10n/default';
import { createPopper } from '@popperjs/core';

import closeSvg from '../../images/icons/close-x.svg'

let activeTooltip = null;

export default class extends Controller {
    static values = {
        events: Object,
        timezone: String
    }

    connect() {
        super.connect();
        this.load();
    }

    async load() {
        const flatpickr = (await import('flatpickr')).default;

        flatpickr(this.element, {
            inline: true,
            appendTo: this.element,
            monthSelectorType: 'static',
            locale: await this.getLocale(),
            onDayCreate: this.handleDayCreate.bind(this),
        });
    }

    async getLocale() {
        switch (document.documentElement.lang.split('-')[0]) {
            case 'lt':
                return Lithuanian;
            default:
                return english;
        }
    }

    handleDayCreate(dObj, dStr, fp, dayElem) {
        if (!this.hasEventsValue) return;

        const date = dayElem.dateObj;
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        if (this.eventsValue[formattedDate]) {
            dayElem.classList.add('has-event');

            const tooltip = document.createElement('div');
            tooltip.classList.add('calendar-tooltip');

            const hideTooltip = () => {
                dayElem.classList.remove('tooltip-active');
                tooltip.style.opacity = '0';
                tooltip.style.visibility = 'hidden';
                if (activeTooltip === tooltip) activeTooltip = null;
            };

            const closeButton = document.createElement('button');
            closeButton.classList.add('tooltip-close');
            closeButton.type = 'button';
            closeButton.innerHTML = `<img src="${closeSvg}" alt="Close">`;

            closeButton.addEventListener('touchstart', function(e) {
                e.preventDefault();
                e.stopPropagation();
                hideTooltip();
            }, true);

            this.eventsValue[formattedDate].forEach(event => {
                const link = document.createElement('a');
                link.href = event.url;
                link.style.fontSize = '14px';
                link.classList.add('tooltip-link');
                link.innerHTML = `${event.title}${event.time ? ` ${event.time}` : ''}`;

                tooltip.appendChild(link);
            });

            tooltip.appendChild(closeButton);
            dayElem.appendChild(tooltip);

            const calendarContainer = this.element;

            const popperInstance = createPopper(dayElem, tooltip, {
                placement: 'top',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 7],
                        },
                    },
                ],
            });

            const showTooltip = () => {
                if (activeTooltip && activeTooltip !== tooltip) {
                    const previousDay = activeTooltip.closest('.has-event');
                    if (previousDay) {
                        previousDay.classList.remove('tooltip-active');
                    }
                    activeTooltip.style.opacity = '0';
                    activeTooltip.style.visibility = 'hidden';
                }

                dayElem.classList.add('tooltip-active');
                tooltip.style.opacity = '1';
                tooltip.style.visibility = 'visible';
                activeTooltip = tooltip;
                popperInstance.update();
            };

            const handleTouch = (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (activeTooltip === tooltip) {
                    return;
                }

                showTooltip();
            };

            tooltip.querySelectorAll('.tooltip-link').forEach(link => {
                link.addEventListener('touchstart', (e) => {
                    e.stopPropagation();
                });

                link.addEventListener('click', (e) => {
                    e.stopPropagation();
                    window.location.href = link.href;
                });
            });

            dayElem.addEventListener('mouseenter', showTooltip);
            dayElem.addEventListener('mouseleave', hideTooltip);

            dayElem.addEventListener('touchstart', handleTouch);

            const clickOutsideHandler = (e) => {
                if (!dayElem.contains(e.target) && !tooltip.contains(e.target)) {
                    hideTooltip();
                }
            };

            document.addEventListener('click', clickOutsideHandler);
            document.addEventListener('touchstart', clickOutsideHandler);

            dayElem.addEventListener('flatpickr:destroy', () => {
                document.removeEventListener('click', clickOutsideHandler);
                document.removeEventListener('touchstart', clickOutsideHandler);
                clearTimeout(touchTimeout);
            });
        }
    }
}
